package com.siriusxm.pia.views.unifiedaggregator.events

import androidx.compose.runtime.*
import com.siriusxm.cmc.utils.copy
import com.siriusxm.cmc.utils.encodeURIComponent
import com.siriusxm.cmc.utils.getString
import com.siriusxm.cmc.utils.prettyPrint
import com.siriusxm.pia.SXMUI
import com.siriusxm.pia.components.DefaultJsonViewRenderer
import com.siriusxm.pia.components.button
import com.siriusxm.pia.components.jsonView
import com.siriusxm.pia.rest.unifiedaggregator.CloudEventChanges
import com.siriusxm.pia.rest.unifiedaggregator.entityId
import com.siriusxm.pia.rest.unifiedaggregator.entityType
import com.siriusxm.pia.views.unifiedaggregator.AggregatorService
import com.siriusxm.pia.views.unifiedaggregator.smithy.aggregatorOptions
import com.siriusxm.pia.views.unifiedaggregator.smithy.aggregatorSmithyEntityView
import com.siriusxm.pia.views.unifiedaggregator.smithy.smithyViewDecoder
import com.siriusxm.pia.views.unifiedaggregator.types.EntityTypeValidationPanel
import unifiedcontent.aggregator.CloudEvent
import unifiedcontent.aggregator.ContentUpdateError
import unifiedcontent.aggregator.ContentUpdateType
import unifiedcontent.aggregator.PartialUpdate
import kotlinx.serialization.json.*
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.A
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text

/**
 * Base interface for rendering cloud events.
 */
interface CloudEventRenderer {
    /**
     * The title of the event
     */
    val title: String

    /**
     * Render the event
     */
    @Composable
    fun render()
}

internal val cloudEventDecoder = Json {
    ignoreUnknownKeys = true
}

@Composable
private fun nullLabel() {
    Span({
        style {
            color(SXMUI.subtleTextColor.value())
            fontStyle("italic")
        }
    }) { Text("null") }
}

private class EventJsonViewRenderer : DefaultJsonViewRenderer(hideEmptyOrNull = false) {
    @Composable
    override fun renderNull(parent: JsonElement?, name: String?, element: JsonNull) {
        nullLabel()
    }
}

private fun eventOptions(aggregator: AggregatorService) = aggregatorOptions(aggregator).apply {
    showEmptyFields = true
    nullValueContent = { nullLabel() }
}

/**
 * Default implementation of a CloudEventRenderer, just takes the event type for
 * the title and renders a generic JSON view.
 */
open class DefaultCloudEventRenderer(val event: CloudEvent) : CloudEventRenderer {
    override val title: String = event.type

    @Composable
    override fun render() {
        (event.data as? JsonObject)?.let {
            jsonView(it, renderer = EventJsonViewRenderer())
        }
    }
}

class ContentUpdateRenderer(
    val aggregator: AggregatorService,
    val event: CloudEvent
) : CloudEventRenderer {
    override val title: String
        get() {
            val updateType = event.tracingcontext?.getString("updateType")?.let {
                ContentUpdateType.fromName(it)
            }
            return when (updateType) {
                ContentUpdateType.ADD -> "Add"
                ContentUpdateType.REMOVE -> "Remove"
                ContentUpdateType.UPDATE -> "Update"
                else -> "Unknown"
            }
        }

    @Composable
    override fun render() {
        event.data?.let {
            Div {
                aggregatorSmithyEntityView(aggregator, it.jsonObject, eventOptions(aggregator))
            }
        }
    }
}

/**
 * The id of the entity this event applies to, for display in type-wide event feeds that span
 * many entity instances. Checked in order: the `modification` payload (partial updates), the
 * entity/modification nested under `update`/`partial` (Invalid Content Update events, mirroring
 * [InvalidContentUpdateRenderer]'s own lookup), then the top-level entity id for event types
 * (e.g. "Content Update", outgoing Atlas events) that carry the entity directly on `data`. Every
 * step is cast with `as?` before use so a malformed or explicit-null payload yields null instead
 * of throwing.
 */
fun CloudEvent.displayEntityId(): String? {
    val root = data as? JsonObject ?: return null
    val updatePayload = (root["update"] as? JsonObject)
        ?: (root["partial"] as? JsonObject)
    val entity = (root["modification"] as? JsonObject)
        ?: (updatePayload?.get("entity") as? JsonObject)
        ?: (updatePayload?.get("modification") as? JsonObject)
        ?: root
    return entity.entityId
}

/**
 * A link to an entity's detail page, shared by the event-feed row/detail views that surface
 * [displayEntityId]. Not a typed NavKey: entities have no NavKey family in this codebase yet
 * (unlike producers), and every existing entity link (EntityList.kt, EntityIdRenderers.kt,
 * FavoritesSection.kt, ContentAuditLog.kt, PartialEditor.kt) already builds the same raw href —
 * introducing one here without migrating those would just add a second, inconsistent pattern.
 */
@Composable
fun EntityLink(id: String) {
    A(href = "#aggregator/entity/${encodeURIComponent(id)}") { Text(id) }
}

class PartialUpdateRenderer(
    val aggregator: AggregatorService,
    val event: CloudEvent
) : CloudEventRenderer {
    override val title: String
        get() {
            val updateType = event.data?.jsonObject?.getString("type")?.let {
                ContentUpdateType.fromName(it)
            }
            return when (updateType) {
                ContentUpdateType.ADD -> "Partial Add"
                ContentUpdateType.REMOVE -> "Partial Remove"
                ContentUpdateType.UPDATE -> "Partial Update"
                else -> "Unknown"
            }
        }

    @Composable
    override fun render() {
        (event.data as? JsonObject)?.let {
            val partial = try {
                smithyViewDecoder.decodeFromJsonElement<PartialUpdate>(it)
            } catch (t: Throwable) {
                t.printStackTrace()
                null
            }
            if (partial != null) {
                partialEventView(aggregator, partial)
            } else {
                jsonView(it, renderer = EventJsonViewRenderer())
            }
        }
    }
}

class AtlasEventRenderer(
    val aggregator: AggregatorService,
    val event: CloudEvent,
    val changes: CloudEventChanges? = null
) : CloudEventRenderer {
    override val title: String
        get() {
            return event.type.substringAfterLast(".")
                .replaceFirstChar { if (it.isLowerCase()) it.titlecase() else it.toString() }
        }

    @Composable
    override fun render() {
        event.data?.let {
            Div {
                diffableSmithyView(aggregator, it.jsonObject, changes, eventOptions(aggregator))
            }
        }
    }
}

/**
 * Renders an Invalid Content Update event, when a producer has published something we don't support.
 */
class InvalidContentUpdateRenderer(
    val aggregator: AggregatorService, event: CloudEvent,
    val showProducer: Boolean = false, val showType: Boolean = true
) :
    DefaultCloudEventRenderer(event) {
    val error: ContentUpdateError? by lazy {
        event.data?.let {
            cloudEventDecoder.decodeFromJsonElement(it)
        }
    }

    private val updatePayload: JsonObject?
        get() {
            val eventData = event.data?.jsonObject
            return (eventData?.get("update") ?: eventData?.get("partial")) as? JsonObject
        }

    private val source: String?
        get() {
            val eventData = event.data?.jsonObject
            return eventData?.getString("source")
        }

    override val title: String
        get() {
            val reason = error?.reason
            val message = when (reason) {
                "Type not allowed" -> buildString {
                    append(reason)
                    if (showType) {
                        val updateEntity = (updatePayload?.get("entity")
                            ?: updatePayload?.get("modification")) as? JsonObject
                        val type = updateEntity?.entityType?.let {
                            aggregator.entityType(it)
                        }
                        if (type != null) {
                            append(", ${type.name}")
                        }
                    }
                    if (showProducer) {
                        val producer = source?.let {
                            aggregator.producerById(it)?.name ?: it
                        }
                        if (producer != null) {
                            append(", $producer")
                        }
                    }
                }

                else -> reason
            }
            return "Invalid content update: $message"
        }

    @Composable
    override fun render() {
        val data = updatePayload

        // State for comparison dialog
        var showComparisonDialog by remember { mutableStateOf(false) }
        var comparisonResult by remember { mutableStateOf<EntityComparisonResult?>(null) }
        var loadingComparison by remember { mutableStateOf(false) }
        var comparisonError by remember { mutableStateOf<String?>(null) }

        if (data != null) {
            jsonView(data.jsonObject.copy {
                source?.let {
                    put("source", it)
                }
            }, renderer = EventJsonViewRenderer())

            val invalidEntity = data.jsonObject.get("entity")
            if (invalidEntity != null) {
                // Try "type" field first, fall back to "entity" field for malformed payloads
                // where producers sent "entity" instead of "type" for the entity type name
                val entityTypeName = invalidEntity.entityType
                    ?: invalidEntity.jsonObject["entity"]?.jsonPrimitive?.contentOrNull
                val entityType = entityTypeName?.let { aggregator.entityType(it) }
                if (entityType != null) {
                    EntityTypeValidationPanel(
                        aggregator = aggregator,
                        entityType = entityType,
                        showJson = true,
                        initialJson = data.toString().prettyPrint(),
                        message = "Validate this message against the API Registry model.",
                        extraActions = { _ ->
                            button {
                                title = "Compare with Authorized"
                                icon = "compare_arrows"
                                enabled = !loadingComparison
                                showProgressOnAction = true
                                action {
                                    loadingComparison = true
                                    comparisonError = null
                                    try {
                                        comparisonResult = compareRejectedWithAuthorized(
                                            aggregator = aggregator,
                                            rejectedPayload = data
                                        )
                                        showComparisonDialog = true
                                    } catch (e: Exception) {
                                        comparisonError = e.message ?: "Failed to fetch authorized version"
                                        console.error("Error comparing entities:", e)
                                    } finally {
                                        loadingComparison = false
                                    }
                                }
                            }

                            comparisonError?.let { error ->
                                Span({
                                    style {
                                        color(SXMUI.stopColor.value())
                                        fontSize(0.9.em)
                                    }
                                }) {
                                    Text(error)
                                }
                            }
                        }
                    )
                }
            }
        }

        // Render comparison dialog if requested
        if (showComparisonDialog && comparisonResult != null) {
            EntityComparisonDialog(
                comparisonResult = comparisonResult!!,
                onClose = { showComparisonDialog = false }
            )
        }
    }
}
